import React, {useCallback, useMemo, useRef} from 'react';
import {
    Box,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {B61_PAGE, B61_SEED_ROWS} from '@/pages/components/audit-program/b-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface B61SignificantMattersViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const ROW_FIELDS = ['ref', 'description', 'c8Ref', 'work', 'conclusion'] as const;

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.75rem',
    py: 0.75,
    px: 0.5,
};
const hintSx = {
    ...cellBorder,
    py: 0.75,
    px: 0.5,
    verticalAlign: 'top' as const,
};
const bodySx = {
    ...cellBorder,
    py: 0.5,
    px: 0.5,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.75,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

function CellInput({
    value,
    onChange,
    multiline,
    align = 'left',
    rows = 2,
}: {
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    align?: 'left' | 'center';
    rows?: number;
}) {
    if (multiline) {
        return (
            <Box
                component="textarea"
                value={value}
                rows={rows}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                sx={{...nativeBaseSx, resize: 'vertical', textAlign: align}}
            />
        );
    }
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeBaseSx, textAlign: align}}
        />
    );
}

function parseRowIds(raw?: string): string[] {
    if (!raw || !String(raw).trim()) return [];
    return String(raw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}

function discoverLegacyRowIds(values: Record<string, string>): string[] {
    const ids = new Set<string>();
    for (const seed of B61_SEED_ROWS) ids.add(seed.id);
    for (const key of Object.keys(values)) {
        const m = /^(r\d+|extra\d+|row_\d+)\./.exec(key);
        if (m) ids.add(m[1]);
    }
    const seedIds: string[] = B61_SEED_ROWS.map((r) => r.id);
    const rest = [...ids].filter((id) => !seedIds.includes(id)).sort();
    return [...seedIds.filter((id) => ids.has(id)), ...rest];
}

function nextRowId(existing: string[]): string {
    let n = 1;
    const set = new Set(existing);
    while (set.has(`row_${n}`)) n += 1;
    return `row_${n}`;
}

function nextRef(existingRefs: string[]): string {
    const nums = existingRefs
        .map((r) => Number.parseInt(String(r).trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0);
    const max = nums.length ? Math.max(...nums) : 0;
    return String(max + 1);
}

/**
 * Excel B6.1 — Summary of Significant Audit Matters.
 * Editable rows with free add/delete (same pattern as C8.2).
 * Hints sit above Work / Conclusion columns (Excel layout); cell chrome matches other Program tables.
 */
export default function B61SignificantMattersView({
    header,
    values,
    onChange,
}: B61SignificantMattersViewProps) {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const set = useCallback((key: string, value: string) => {
        onChangeRef.current(key, value);
    }, []);

    const rowIds = useMemo(() => {
        const explicit = parseRowIds(values.rowIds);
        if (explicit.length) return explicit;
        return discoverLegacyRowIds(values);
    }, [values]);

    const f = (id: string, field: (typeof ROW_FIELDS)[number]) => values[`${id}.${field}`] ?? '';

    const handleAdd = () => {
        const id = nextRowId(rowIds);
        const ref = nextRef(rowIds.map((rid) => f(rid, 'ref')));
        set('rowIds', [...rowIds, id].join(','));
        set(`${id}.ref`, ref);
        set(`${id}.description`, '');
        set(`${id}.c8Ref`, '');
        set(`${id}.work`, '');
        set(`${id}.conclusion`, '');
    };

    const handleDelete = (id: string) => {
        if (rowIds.length <= 1) return;
        set('rowIds', rowIds.filter((x) => x !== id).join(','));
        for (const field of ROW_FIELDS) {
            set(`${id}.${field}`, '');
        }
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="B6.1"
                values={values}
                onChange={set}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1.5, mt: 0.5}}
            >
                {B61_PAGE.title}
            </Typography>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 1000, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 56}}/>
                        <col style={{width: 200}}/>
                        <col style={{width: 110}}/>
                        <col/>
                        <col/>
                        <col style={{width: 48}}/>
                    </colgroup>
                    <TableHead>
                        {/* Excel: hints aligned under Work / Conclusion only */}
                        <TableRow>
                            <TableCell sx={hintSx} colSpan={3}/>
                            <TableCell sx={hintSx}>
                                <Typography variant="caption" color="text.secondary" sx={{fontStyle: 'italic', lineHeight: 1.4, display: 'block'}}>
                                    {B61_PAGE.workHint}
                                </Typography>
                            </TableCell>
                            <TableCell sx={hintSx}>
                                <Typography variant="caption" color="text.secondary" sx={{fontStyle: 'italic', lineHeight: 1.4, display: 'block'}}>
                                    {B61_PAGE.conclusionHint}
                                </Typography>
                            </TableCell>
                            <TableCell sx={hintSx}/>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={headSx} align="center">Ref</TableCell>
                            <TableCell sx={headSx}>Description of matter</TableCell>
                            <TableCell sx={headSx} align="center">Ref to C8.2 / C8.3</TableCell>
                            <TableCell sx={headSx}>Work undertaken</TableCell>
                            <TableCell sx={headSx}>Conclusion</TableCell>
                            <TableCell sx={headSx}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(id, 'ref')}
                                        onChange={(v) => set(`${id}.ref`, v)}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(id, 'description')}
                                        onChange={(v) => set(`${id}.description`, v)}
                                        multiline
                                        rows={2}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(id, 'c8Ref')}
                                        onChange={(v) => set(`${id}.c8Ref`, v)}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(id, 'work')}
                                        onChange={(v) => set(`${id}.work`, v)}
                                        multiline
                                        rows={3}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(id, 'conclusion')}
                                        onChange={(v) => set(`${id}.conclusion`, v)}
                                        multiline
                                        rows={3}
                                    />
                                </TableCell>
                                <TableCell sx={{...bodySx, px: 0}} align="center">
                                    <IconButton
                                        size="small"
                                        aria-label="delete row"
                                        onClick={() => handleDelete(id)}
                                        disabled={rowIds.length <= 1}
                                        sx={{color: 'text.secondary'}}
                                    >
                                        <DeleteOutlineIcon fontSize="small"/>
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 1.5}}>
                <Button size="small" startIcon={<AddIcon/>} onClick={handleAdd} variant="outlined">
                    Add row
                </Button>
            </Box>
        </Box>
    );
}
